import React, { useState, useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { ColumnSettingItem } from '@/components/molecules/dashboard/column-setting-item'
import { DashboardReservationColumn, DashboardReservationFilterType } from '@/models/dashboard/dashboard-reservation'
import { updateDashboardSettings } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'

type Props = {
  defaultColumns: DashboardReservationColumn[]
  defaultFilter: DashboardReservationFilterType
  onClose: () => void
  onSave: () => void
}

export const CustomizeModal: React.FC<Props> = ({ defaultColumns, defaultFilter, onClose, onSave }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const [isLoading, setIsLoading] = useState(false)
  const [filterType, setFilterType] = useState<DashboardReservationFilterType>(defaultFilter)
  const [columns, setColumns] = useState<DashboardReservationColumn[]>(defaultColumns)

  /**
   * 現在のカラム設定を sortOrder でソート
   * UI上での表示順を確定する際に使用
   */
  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [columns])

  /**
   * 編集可能なカラムのみを抽出
   * 固定カラム（isFixed=true）を除外し、ドラッグ&ドロップ対象とするカラムのみを取得
   */
  const editableColumns = useMemo(() => {
    return columns.filter(col => !col.isFixed)
  }, [columns])

  /**
   * ドラッグ&ドロップによるカラム並び替え機能
   * 編集可能なカラムのみを対象とし、固定カラムの sortOrder は維持する
   */
  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom<DashboardReservationColumn>({
    list: editableColumns,
    onChange: newEditableColumns => {
      // 固定カラムを先に配置し、その後に編集可能カラムを配置することで
      // 固定カラムの位置を常に先頭に保つ
      const fixedColumns = columns.filter(col => col.isFixed).sort((a, b) => a.sortOrder - b.sortOrder)
      const fixedCount = fixedColumns.length
      const updatedEditableColumns = newEditableColumns.map((col, index) => ({
        ...col,
        sortOrder: fixedCount + index + 1,
      }))
      setColumns([...fixedColumns, ...updatedEditableColumns])
    },
    key: 'id',
  })

  /**
   * 指定されたカラムの表示/非表示を切り替え
   * @param columnId カラムID
   */
  const handleToggleVisibility = (columnId: string) => {
    setColumns(prevColumns => prevColumns.map(col => (col.id === columnId ? { ...col, isVisible: !col.isVisible } : col)))
  }

  /**
   * カスタマイズ内容をサーバーに保存
   * - カラムの表示/非表示設定
   * - カラムの並び替え順序
   * - メモフィルター設定（全表示 / メモ有りのみ）
   */
  const handleSave = async () => {
    try {
      setIsLoading(true)
      // ユーザーが行ったドラッグ&ドロップの結果を反映したカラムリストを sortOrder でソート
      const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder)
      // sortOrder を 1 から連番に再計算
      const columnSettings = sorted.map((col, index) => ({
        columnName: col.columnName,
        sortOrder: index + 1,
        isVisible: col.isVisible,
      }))

      // サーバーに設定を送信
      await updateDashboardSettings({
        columnSettings,
        filterSetting: {
          memoFilterType: filterType,
        },
      })

      onSave()
      onClose()
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * フィルター設定のラジオボタンアイテム
   * - all: 全ての予約を表示
   * - withMemo: メモが存在する予約のみを表示
   */
  const radioItems = [
    { value: 'all' as DashboardReservationFilterType, label: t('dashboard.allReservations') },
    { value: 'withMemo' as DashboardReservationFilterType, label: t('dashboard.withMemo') },
  ]

  return (
    <Modal customCss={modalStyle}>
      <ModalHeader>
        <div css={headerContentStyle}>
          <span>{t('dashboard.customizeModalTitle')}</span>
          <button css={closeButtonStyle} onClick={onClose}>
            ×
          </button>
        </div>
      </ModalHeader>
      <ModalBody customCss={modalBodyStyle}>
        {/* フィルター設定セクション */}
        <div css={sectionStyle}>
          <div css={sectionTitleStyle}>{t('dashboard.targetReservations')}</div>
          <RadioGroupField
            value={filterType}
            items={radioItems}
            onChange={e => setFilterType(e.target.value as DashboardReservationFilterType)}
            style={{ radioSize: 18, labelMargin: 8 }}
            itemWrapperCss={css({ marginRight: 10 })}
          />
        </div>

        {/* セクション区切り線 */}
        <div css={dividerStyle} />

        {/* カラム設定セクション */}
        <div css={sectionStyle}>
          <div css={sectionTitleStyle}>{t('dashboard.itemCustomization')}</div>
          <div css={columnListStyle}>
            {sortedColumns.map(column => (
              <ColumnSettingItem
                key={column.id}
                column={column}
                onToggleVisibility={() => handleToggleVisibility(column.id)}
                dragStart={dragStart}
                dragOver={dragOver}
                dragDrop={dragDrop}
                handleTouchStart={handleTouchStart}
                handleTouchMove={handleTouchMove}
                handleTouchEnd={handleTouchEnd}
              />
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter customCss={modalFooterStyle}>
        <Button buttonType={ButtonType.Secondary} height={36} width={110} onClick={onClose}>
          {t('dashboard.cancel')}
        </Button>
        <Button buttonType={ButtonType.Primary} height={36} width={110} onClick={handleSave} loading={isLoading}>
          {t('dashboard.save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css({
  width: 680,
  maxHeight: '85vh',
  height: 'auto',
  minHeight: 600,
})

const headerContentStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
})

const closeButtonStyle = css({
  background: 'none',
  border: 'none',
  fontSize: 24,
  color: '#676767',
  cursor: 'pointer',
  padding: 0,
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.7,
  },
})

const modalBodyStyle = css({
  padding: '26px 32px',
  backgroundColor: '#F2F2F2',
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
})

const sectionStyle = css({
  marginBottom: 24,
  '&:last-child': {
    marginBottom: 0,
  },
})

const sectionTitleStyle = css({
  fontSize: 16,
  fontWeight: 'bold',
  color: '#272727',
  marginBottom: 20,
})

const dividerStyle = css({
  height: 1,
  backgroundColor: '#E0E0E0',
  margin: '24px 0',
  width: '100%',
})

const columnListStyle = css({
  maxHeight: 500,
  overflowY: 'auto',
  paddingRight: 8,
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: '#F5F5F5',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#CCCCCC',
    borderRadius: 4,
    '&:hover': {
      background: '#999999',
    },
  },
})

const modalFooterStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
})
